// 📂 src/pages/CameraDetail/components/TimelineController.jsx
// Enterprise 24-hour timeline — canvas-rendered, zoomable, Milestone XProtect grade

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { FiZoomIn, FiZoomOut, FiCrosshair } from "react-icons/fi";

/* ─── Color palette (matches EnterpriseDVRPlayer dark theme) ─── */
const C = {
  bg: "#0f0f0f",
  trackBg: "#1a1a1a",
  recording: "#2563eb",       // blue-600
  recordingHover: "#3b82f6",  // blue-500
  gap: "rgba(239,68,68,0.15)",// subtle red for gaps
  event: "#ef4444",           // red-500
  eventRecent: "#f97316",     // orange-500
  playhead: "#ffffff",
  hover: "rgba(255,255,255,0.6)",
  gridLine: "rgba(255,255,255,0.06)",
  gridText: "rgba(255,255,255,0.3)",
  gridTextBright: "rgba(255,255,255,0.5)",
  selection: "rgba(59,130,246,0.15)",
};

const ZOOM_LEVELS = [1, 2, 4, 6, 12, 24]; // 24h, 12h, 6h, 4h, 2h, 1h view
const TIMELINE_HEIGHT = 72;
const RECORDING_BAR_H = 16;
const EVENT_MARKER_H = 8;

export default function TimelineController({
  date,
  currentTime,
  onSeek,
  recordings = [],
  detections = [],
  timezone = "America/Vancouver",
  bookmarks = [],
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [dimensions, setDimensions] = useState({ width: 800, height: TIMELINE_HEIGHT });
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredTime, setHoveredTime] = useState(null);
  const [hoveredX, setHoveredX] = useState(null);

  // Zoom & pan
  const [zoomIdx, setZoomIdx] = useState(0); // index into ZOOM_LEVELS
  const [panOffset, setPanOffset] = useState(0); // hours offset from midnight

  const zoomFactor = ZOOM_LEVELS[zoomIdx];
  const visibleHours = 24 / zoomFactor;

  /* ─── Day boundaries ─── */
  const dayStart = useMemo(() => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [date]);

  const dayEnd = useMemo(() => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [date]);

  /* ─── Visible window ─── */
  const viewStart = useMemo(() => {
    const d = new Date(dayStart);
    d.setMinutes(d.getMinutes() + panOffset * 60);
    return d;
  }, [dayStart, panOffset]);

  const viewEnd = useMemo(() => {
    const d = new Date(viewStart);
    d.setMinutes(d.getMinutes() + visibleHours * 60);
    return d;
  }, [viewStart, visibleHours]);

  /* ─── Resize observer ─── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      if (width > 0) setDimensions({ width, height: TIMELINE_HEIGHT });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ─── Coordinate conversions ─── */
  const timeToX = useCallback((time) => {
    const t = new Date(time).getTime();
    const s = viewStart.getTime();
    const e = viewEnd.getTime();
    return ((t - s) / (e - s)) * dimensions.width;
  }, [viewStart, viewEnd, dimensions.width]);

  const xToTime = useCallback((x) => {
    const s = viewStart.getTime();
    const e = viewEnd.getTime();
    const t = s + (x / dimensions.width) * (e - s);
    // Clamp to day
    return new Date(Math.max(dayStart.getTime(), Math.min(dayEnd.getTime(), t)));
  }, [viewStart, viewEnd, dimensions.width, dayStart, dayEnd]);

  /* ─── Canvas draw ─── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = dimensions.width * dpr;
    canvas.height = dimensions.height * dpr;
    canvas.style.width = `${dimensions.width}px`;
    canvas.style.height = `${dimensions.height}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    const W = dimensions.width;
    const H = dimensions.height;

    // Background
    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, W, H);

    // Recording track background
    const trackY = 8;
    ctx.fillStyle = C.trackBg;
    ctx.roundRect(0, trackY, W, RECORDING_BAR_H, 2);
    ctx.fill();

    // ── Recording coverage bars ──
    recordings.forEach((rec) => {
      const startX = timeToX(rec.start_time);
      const endTime = rec.end_time || new Date(new Date(rec.start_time).getTime() + 5 * 60000).toISOString();
      const endX = timeToX(endTime);
      const w = Math.max(endX - startX, 1);

      if (startX + w < 0 || startX > W) return; // off-screen

      ctx.fillStyle = C.recording;
      ctx.beginPath();
      ctx.roundRect(Math.max(0, startX), trackY, Math.min(w, W - startX), RECORDING_BAR_H, 1);
      ctx.fill();
    });

    // ── Gap indicators (red tint between recordings) ──
    for (let i = 1; i < recordings.length; i++) {
      const prevEnd = recordings[i - 1].end_time
        ? new Date(recordings[i - 1].end_time).getTime()
        : new Date(recordings[i - 1].start_time).getTime() + 5 * 60000;
      const nextStart = new Date(recordings[i].start_time).getTime();
      if (nextStart - prevEnd > 2 * 60000) {
        const gapStartX = timeToX(new Date(prevEnd));
        const gapEndX = timeToX(new Date(nextStart));
        ctx.fillStyle = C.gap;
        ctx.fillRect(gapStartX, trackY, gapEndX - gapStartX, RECORDING_BAR_H);
      }
    }

    // ── Event markers row ──
    const eventY = trackY + RECORDING_BAR_H + 4;
    detections.forEach((det) => {
      const ts = det.timestamp || det.start_time;
      if (!ts) return;
      const x = timeToX(ts);
      if (x < -4 || x > W + 4) return;

      ctx.fillStyle = det.isRecent ? C.eventRecent : C.event;
      ctx.beginPath();
      // Triangle marker
      ctx.moveTo(x, eventY);
      ctx.lineTo(x - 3, eventY + EVENT_MARKER_H);
      ctx.lineTo(x + 3, eventY + EVENT_MARKER_H);
      ctx.closePath();
      ctx.fill();
    });

    // ── Bookmark markers ──
    (bookmarks || []).forEach((bm) => {
      const x = timeToX(bm.timestamp);
      if (x < -4 || x > W + 4) return;
      ctx.fillStyle = "#eab308"; // yellow
      ctx.beginPath();
      ctx.arc(x, eventY + EVENT_MARKER_H / 2, 3, 0, Math.PI * 2);
      ctx.fill();
    });

    // ── Time grid ──
    const gridY = eventY + EVENT_MARKER_H + 6;
    const hoursVisible = visibleHours;
    // Determine grid interval based on zoom
    let interval = 3; // hours
    if (hoursVisible <= 4) interval = 0.5;
    else if (hoursVisible <= 8) interval = 1;
    else if (hoursVisible <= 16) interval = 2;

    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    const vStart = viewStart.getTime();
    const vEnd = viewEnd.getTime();
    const intervalMs = interval * 3600000;
    const firstTick = Math.ceil(dayStart.getTime() / intervalMs) * intervalMs;

    for (let t = firstTick; t <= dayEnd.getTime(); t += intervalMs) {
      if (t < vStart || t > vEnd) continue;
      const x = timeToX(new Date(t));

      // Grid line
      ctx.strokeStyle = C.gridLine;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, trackY);
      ctx.lineTo(x, gridY - 2);
      ctx.stroke();

      // Label
      const d = new Date(t);
      const hr = d.getHours();
      const min = d.getMinutes();
      const label = min === 0
        ? `${hr.toString().padStart(2, "0")}:00`
        : `${hr.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;

      ctx.font = "10px ui-monospace, SFMono-Regular, monospace";
      ctx.fillStyle = hr % 6 === 0 && min === 0 ? C.gridTextBright : C.gridText;
      ctx.fillText(label, x, gridY);
    }

    // ── Hover line ──
    if (hoveredX != null && !isDragging) {
      ctx.strokeStyle = C.hover;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(hoveredX, 0);
      ctx.lineTo(hoveredX, H);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // ── Playhead ──
    if (currentTime) {
      const px = timeToX(currentTime);
      if (px >= -2 && px <= W + 2) {
        // Playhead line
        ctx.strokeStyle = C.playhead;
        ctx.lineWidth = 2;
        ctx.shadowColor = "rgba(255,255,255,0.4)";
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px, H);
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Playhead diamond
        ctx.fillStyle = C.playhead;
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px - 5, -1);
        ctx.lineTo(px - 5, 5);
        ctx.lineTo(px, 8);
        ctx.lineTo(px + 5, 5);
        ctx.lineTo(px + 5, -1);
        ctx.closePath();
        ctx.fill();
      }
    }
  }, [
    dimensions, recordings, detections, bookmarks, currentTime,
    viewStart, viewEnd, visibleHours, hoveredX, isDragging, timeToX, dayStart, dayEnd,
  ]);

  /* ─── Mouse handlers ─── */
  const getX = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    return rect ? e.clientX - rect.left : 0;
  };

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    const time = xToTime(getX(e));
    onSeek?.(time);
  }, [xToTime, onSeek]);

  const handleMouseMove = useCallback((e) => {
    const x = getX(e);
    setHoveredX(x);
    setHoveredTime(xToTime(x));
    if (isDragging) onSeek?.(xToTime(x));
  }, [xToTime, isDragging, onSeek]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);
  const handleMouseLeave = useCallback(() => {
    setHoveredTime(null);
    setHoveredX(null);
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      const up = () => setIsDragging(false);
      window.addEventListener("mouseup", up);
      return () => window.removeEventListener("mouseup", up);
    }
  }, [isDragging]);

  /* ─── Touch support ─── */
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = touch.clientX - rect.left;
    setIsDragging(true);
    onSeek?.(xToTime(x));
  }, [xToTime, onSeek]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = touch.clientX - rect.left;
    if (isDragging) onSeek?.(xToTime(x));
  }, [xToTime, isDragging, onSeek]);

  /* ─── Zoom controls ─── */
  const zoomIn = () => setZoomIdx((i) => Math.min(i + 1, ZOOM_LEVELS.length - 1));
  const zoomOut = () => {
    setZoomIdx((i) => {
      const next = Math.max(i - 1, 0);
      if (next === 0) setPanOffset(0);
      return next;
    });
  };
  const resetView = () => { setZoomIdx(0); setPanOffset(0); };

  // Auto-pan to keep playhead visible
  useEffect(() => {
    if (!currentTime || zoomFactor <= 1) return;
    const ct = new Date(currentTime);
    const ctHours = ct.getHours() + ct.getMinutes() / 60;
    const halfView = visibleHours / 2;
    if (ctHours < panOffset || ctHours > panOffset + visibleHours) {
      setPanOffset(Math.max(0, Math.min(24 - visibleHours, ctHours - halfView)));
    }
  }, [currentTime, zoomFactor, visibleHours, panOffset]);

  // Scroll to pan when zoomed
  const handleWheel = useCallback((e) => {
    if (zoomFactor <= 1) return;
    e.preventDefault();
    const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
    const panStep = visibleHours * 0.1;
    setPanOffset((prev) => Math.max(0, Math.min(24 - visibleHours, prev + (delta > 0 ? panStep : -panStep))));
  }, [zoomFactor, visibleHours]);

  return (
    <div className="bg-[#0f0f0f] border-t border-white/5">
      {/* Zoom controls */}
      {zoomFactor > 1 && (
        <div className="flex items-center justify-between px-3 py-1 border-b border-white/5">
          <div className="flex items-center gap-1">
            <button onClick={zoomOut} className="tl-btn" title="Zoom out">
              <FiZoomOut className="w-3 h-3" />
            </button>
            <span className="text-[10px] text-gray-500 font-mono w-12 text-center">
              {visibleHours < 1 ? `${Math.round(visibleHours * 60)}m` : `${visibleHours}h`} view
            </span>
            <button onClick={zoomIn} className="tl-btn" disabled={zoomIdx >= ZOOM_LEVELS.length - 1} title="Zoom in">
              <FiZoomIn className="w-3 h-3" />
            </button>
            <button onClick={resetView} className="tl-btn ml-1" title="Reset to 24h">
              <FiCrosshair className="w-3 h-3" />
            </button>
          </div>
          {/* Pan position indicator */}
          <div className="flex items-center gap-2">
            <div className="w-32 h-1 bg-white/5 rounded-full relative">
              <div
                className="absolute top-0 h-1 bg-blue-500/40 rounded-full"
                style={{
                  left: `${(panOffset / 24) * 100}%`,
                  width: `${(visibleHours / 24) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Canvas timeline */}
      <div
        ref={containerRef}
        className="relative cursor-crosshair select-none"
        style={{ height: `${TIMELINE_HEIGHT}px` }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setIsDragging(false)}
        onWheel={handleWheel}
        onDoubleClick={zoomFactor < ZOOM_LEVELS[ZOOM_LEVELS.length - 1] ? zoomIn : resetView}
      >
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

        {/* Hover tooltip */}
        {hoveredTime && !isDragging && hoveredX != null && (
          <div
            className="absolute pointer-events-none z-10"
            style={{
              left: `${Math.max(40, Math.min(hoveredX, dimensions.width - 40))}px`,
              bottom: "100%",
              transform: "translateX(-50%)",
              marginBottom: "4px",
            }}
          >
            <div className="bg-black/95 border border-white/10 text-white text-[11px] font-mono px-2.5 py-1 rounded shadow-xl">
              {hoveredTime.toLocaleTimeString("en-CA", {
                hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: timezone,
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bottom zoom bar (always visible, clickable to zoom) */}
      <div className="flex items-center justify-between px-3 py-1 border-t border-white/5">
        <div className="flex items-center gap-3 text-[10px] text-gray-600">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-1.5 bg-blue-600 rounded-sm inline-block" />
            Recording
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-0 h-0 border-l-[3px] border-r-[3px] border-b-[5px] border-l-transparent border-r-transparent border-b-red-500 inline-block" />
            Events
          </div>
          {(bookmarks?.length > 0) && (
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full inline-block" />
              Bookmarks
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={zoomOut} className="tl-btn" disabled={zoomIdx <= 0}><FiZoomOut className="w-3 h-3" /></button>
          <button onClick={zoomIn} className="tl-btn" disabled={zoomIdx >= ZOOM_LEVELS.length - 1}><FiZoomIn className="w-3 h-3" /></button>
        </div>
      </div>

      <style>{`
        .tl-btn {
          display: flex; align-items: center; justify-content: center;
          width: 22px; height: 22px; border-radius: 4px;
          color: rgba(255,255,255,0.4); background: transparent; border: none;
          cursor: pointer; transition: all 0.15s;
        }
        .tl-btn:hover { color: white; background: rgba(255,255,255,0.08); }
        .tl-btn:disabled { opacity: 0.2; cursor: not-allowed; }
        .tl-btn:disabled:hover { background: transparent; }
      `}</style>
    </div>
  );
}
